import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDownIcon } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const selectTriggerVariants = cva(
    'flex w-fit items-center justify-between gap-2 whitespace-nowrap rounded-2 outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[slot=select-value]:*:flex data-[slot=select-value]:*:items-center data-[slot=select-value]:*:gap-2 data-[slot=select-value]:*:truncate [&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'px-4 py-3',
                small: 'px-3 py-2',
                medium: 'px-6 py-3',
                wide: 'px-9 py-3',
                large: 'px-8 py-4',
            },
            text: {
                default: 'text-base leading-none',
                small: 'text-3.5 leading-none',
                medium: 'text-base font-medium leading-none file:font-medium data-[placeholder]:font-medium',
            },
            color: {
                default:
                    'border border-gray-400 bg-black/5 text-gray-900 data-[placeholder]:text-gray-400',
                lightOutline:
                    'border border-gray-200 bg-white text-gray-900 data-[placeholder]:text-gray-500/70',
                gray: 'border border-gray-100 bg-gray-100 text-gray-900 data-[placeholder]:text-gray-900/50',
                contrast:
                    'border border-white bg-white text-gray-900 data-[placeholder]:text-gray-900/50',
                transparent:
                    'border-none bg-transparent text-gray-900 data-[placeholder]:text-gray-900/50',
            },
            focusStyle: {
                default:
                    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                none: '',
            },
        },
        defaultVariants: {
            variant: 'default',
            text: 'default',
            color: 'default',
            focusStyle: 'default',
        },
    },
);

const selectTriggerIconVariants = cva('', {
    variants: {
        iconStyle: {
            default: 'size-4 opacity-50',
            largeBorderRounded: 'size-5 rounded-full border border-black',
        },
    },
    defaultVariants: {
        iconStyle: 'default',
    },
});

export const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
        VariantProps<typeof selectTriggerVariants> &
        VariantProps<typeof selectTriggerIconVariants> & {
            className?: string;
            hideIcon?: boolean;
            iconClassName?: string;
        }
>(
    (
        {
            children,
            className,
            variant,
            text,
            color,
            focusStyle,
            iconStyle,
            hideIcon = false,
            iconClassName,
            ...props
        },
        ref,
    ) => (
        <SelectPrimitive.Trigger
            ref={ref}
            data-slot="select-trigger"
            className={cn(
                selectTriggerVariants({
                    variant,
                    text,
                    color,
                    focusStyle,
                }),
                className,
            )}
            {...props}
        >
            {children}
            {!hideIcon && (
                <SelectPrimitive.Icon asChild>
                    <ChevronDownIcon
                        className={cn(
                            selectTriggerIconVariants({ iconStyle }),
                            iconClassName,
                        )}
                    />
                </SelectPrimitive.Icon>
            )}
        </SelectPrimitive.Trigger>
    ),
);

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
